// Feature flag definitions and utilities
export const FEATURE_FLAGS = {
  TABLE_ROUND_UI: 'featureFlag_tableRoundUI',
  IOS_VIEWPORT_WORKAROUND: 'featureFlag_iosViewportWorkaround',
};

export const FLAG_DEFAULTS = {
  [FEATURE_FLAGS.TABLE_ROUND_UI]: true, // Table layout is the default UI
  /**
   * Temporary compatibility workaround for native iOS browser toolbar offsets.
   * Reported on iOS 27.0.1: Brave could leave the page scrolled about 60 CSS px
   * down, hiding the header/hamburger and clipping team headings. It happens
   * on the first visit and on pull-to-refresh; scrolling down then up recovered
   * it. Google's iOS app (GSA in-app browser) also hid the header and could retain
   * its native offset through gestures and Home / Continue Game / Back navigation.
   * Standalone iOS Safari and Chrome, plus Android Chrome, did not show the
   * reported issue. iPad behavior has not been physically verified.
   *
   * When enabled, scrollRestoration.ts owns native history restoration and
   * compensates measured positive offsets during an iOS at-top guard. The
   * guard covers the first visit and a reload; a saved position further down
   * the page is restored instead. Refresh used to be the only load that
   * recovered, so the first paint stayed clipped until then.
   * Brave and GSA retain compensation through transient overscroll and across
   * routes until a settled true top. FitStage excludes this padding from its
   * available-height calculation so recovery does not squash/reshape the table.
   * Browser-specific viewport behavior is the working diagnosis, not a confirmed
   * upstream defect; this is deliberately removable compatibility code.
   *
   * Read once at startup: set this localStorage flag to false and reload to
   * disable recovery AND the manual-history override; ordinary saved-position
   * restoration remains. Change this default to disable for a release (explicit
   * local overrides take precedence). This is not a remote kill switch.
   * After Brave/Google updates, validate repeated pulls, normal refresh,
   * Home/Continue/Back, keyboard and rotation with the flag off on real devices.
   * If no longer needed, remove compensation and its FitStage coupling together.
   * See docs/ios-viewport-workaround.md for rollback and retirement instructions.
   */
  [FEATURE_FLAGS.IOS_VIEWPORT_WORKAROUND]: true,
};

export const getFeatureFlag = (flagKey: string) => {
  const item = localStorage.getItem(flagKey);
  if (item === null) return FLAG_DEFAULTS[flagKey] ?? false;
  return JSON.parse(item);
};

export type UiMode = 'table' | 'classic';

/** The round layout this device is using, as reported to analytics. */
export const getUiMode = (): UiMode =>
  getFeatureFlag(FEATURE_FLAGS.TABLE_ROUND_UI) ? 'table' : 'classic';

export const setFeatureFlag = (flagKey: string, value: boolean) => {
  localStorage.setItem(flagKey, JSON.stringify(value));
  window.dispatchEvent(
    new CustomEvent('feature-flag-changed', {
      detail: { key: flagKey, value },
    }),
  );
};
